import { BankOutlined, DownOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Collapse, Drawer, Empty, Input, List, Space, Spin, Tooltip, Typography } from "antd";
import type { InputRef } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { apiClient } from "../../api/apiClient";
import { unwrapApiResponse } from "../utils/apiResponse";

type BankRow = Record<string, unknown>;
type BankAccountRow = Record<string, unknown>;
type BankGroupRow = BankRow & { accounts: BankAccountRow[] };

function readText(row: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim()) return String(value);
  }
  return undefined;
}

function readId(row: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "string" || typeof value === "number") return value;
  }
  return undefined;
}

function bankId(bank: BankRow) {
  return readId(bank, ["id", "Id"]) ?? "";
}

function bankName(bank: BankRow) {
  return readText(bank, ["name", "Name", "bankName", "BankName"]) ?? `Bank #${bankId(bank)}`;
}

function bankCode(bank: BankRow) {
  return readText(bank, ["code", "Code"]);
}

function accountId(account: BankAccountRow) {
  return readId(account, ["id", "Id"]) ?? "";
}

function accountBankId(account: BankAccountRow, bank: BankRow) {
  return readId(account, ["bankId", "BankId"]) ?? bankId(bank);
}

function accountBankName(account: BankAccountRow, bank: BankRow) {
  return readText(account, ["bankName", "BankName"]) ?? bankName(bank);
}

function accountDisplay(account: BankAccountRow, fallbackBankLabel?: string) {
  const number = readText(account, ["accountNumber", "AccountNumber"]);
  const name = readText(account, ["accountName", "AccountName"]);
  if (number && name) return `${number} - ${name}`;
  if (number) return number;
  if (name && name !== fallbackBankLabel) return name;

  const composite = readText(account, ["name", "Name"]);
  if (composite && composite !== fallbackBankLabel) {
    const bankPrefix = fallbackBankLabel ? `${fallbackBankLabel} - ` : "";
    return bankPrefix && composite.startsWith(bankPrefix)
      ? composite.slice(bankPrefix.length)
      : composite;
  }

  return `Account #${accountId(account)}`;
}

function splitAccountLabel(accountLabel?: string) {
  if (!accountLabel) return { number: "", name: "" };
  const [number, ...nameParts] = accountLabel.split(" - ");
  return {
    number: number?.trim() ?? "",
    name: nameParts.join(" - ").trim()
  };
}

function accountDescription(account: BankAccountRow) {
  const number = readText(account, ["accountNumber", "AccountNumber"]);
  const name = readText(account, ["accountName", "AccountName"]);
  const ledgerId = readId(account, ["accountLedgerId", "AccountLedgerId"]);
  return [
    number && name ? name : undefined,
    ledgerId ? `Ledger #${ledgerId}` : undefined
  ].filter(Boolean).join(" - ");
}

function groupAccountsByBank(accounts: BankAccountRow[]): BankGroupRow[] {
  const groups = new Map<string, BankGroupRow>();
  accounts.forEach((account) => {
    const id = readId(account, ["bankId", "BankId"]) ?? readText(account, ["bankName", "BankName"]) ?? "unknown";
    const key = String(id).trim().toLowerCase();
    const existing = groups.get(key);
    if (existing) {
      existing.accounts.push(account);
      return;
    }

    groups.set(key, {
      id,
      name: readText(account, ["bankName", "BankName"]) ?? `Bank #${id}`,
      accounts: [account]
    });
  });

  return Array.from(groups.values())
    .sort((a, b) => bankName(a).localeCompare(bankName(b)));
}

export function BankAccountGroupedLookup({
  bankLabel,
  accountLabel,
  accountNumber,
  accountName,
  disabled,
  showSearchButton = false,
  onSelect
}: {
  bankLabel?: string;
  accountLabel?: string;
  accountNumber?: string;
  accountName?: string;
  disabled?: boolean;
  showSearchButton?: boolean;
  onSelect: (value: {
    bankId: string | number;
    bankLabel: string;
    bankAccountId: string | number;
    bankAccountLabel: string;
    bankAccountNumber?: string;
    bankAccountName?: string;
    accountLedgerId?: string | number;
  }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [activeBankKey, setActiveBankKey] = useState<string>();
  const [accountFocusBankKey, setAccountFocusBankKey] = useState<string>();
  const searchRef = useRef<InputRef>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const banksQuery = useQuery({
    queryKey: ["bank-account-grouped-banks"],
    enabled: open,
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await apiClient.get("/banks/accounts");
      const rows = unwrapApiResponse<BankAccountRow[]>(data) ?? [];
      return groupAccountsByBank(rows);
    }
  });

  const banks = useMemo(() => (banksQuery.data ?? []).filter((bank) => {
    if (!q.trim()) return true;
    const accountText = bank.accounts
      .map((account) => `${accountDisplay(account, bankName(bank))} ${accountDescription(account)}`)
      .join(" ");
    return `${bankName(bank)} ${bankCode(bank) ?? ""} ${accountText}`.toLowerCase().includes(q.toLowerCase());
  }), [banksQuery.data, q]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => searchRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (!banks.length) {
      setActiveBankKey(undefined);
      return;
    }

    const currentExists = banks.some((bank) => String(bankId(bank)) === activeBankKey);
    if (!currentExists) setActiveBankKey(String(bankId(banks[0])));
  }, [activeBankKey, banks]);

  const parsedAccount = splitAccountLabel(accountLabel);
  const displayNumber = accountNumber || parsedAccount.number;
  const displayName = accountName || parsedAccount.name;
  const tooltip = [
    displayNumber ? `A/C Number: ${displayNumber}` : undefined,
    displayName ? `A/C Name: ${displayName}` : undefined,
    bankLabel ? `Bank: ${bankLabel}` : undefined
  ].filter(Boolean).join("\n");

  const focusTrigger = () => {
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const openDrawer = () => {
    setOpen(true);
  };

  const closeDrawer = () => {
    setOpen(false);
    setAccountFocusBankKey(undefined);
    focusTrigger();
  };

  const handleBankSearchKeyDown = (event: KeyboardEvent) => {
    if (!banks.length) return;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const currentIndex = Math.max(0, banks.findIndex((bank) => String(bankId(bank)) === activeBankKey));
      const nextIndex = event.key === "ArrowDown"
        ? Math.min(currentIndex + 1, banks.length - 1)
        : Math.max(currentIndex - 1, 0);
      const nextKey = String(bankId(banks[nextIndex]));
      setActiveBankKey(nextKey);
      setAccountFocusBankKey(undefined);
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const nextKey = activeBankKey ?? String(bankId(banks[0]));
      setActiveBankKey(nextKey);
      setAccountFocusBankKey(nextKey);
    }
  };

  return (
    <>
      <Space.Compact className="smart-lookup" block>
        <Tooltip title={tooltip || undefined} placement="topLeft">
          <div
            ref={triggerRef}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            className={`bank-account-selected-input${disabled ? " is-disabled" : ""}${displayNumber ? " has-value" : ""}`}
            onClick={() => !disabled && openDrawer()}
            onKeyDown={(event) => {
              if (!disabled && event.key === "Enter") {
                event.preventDefault();
                openDrawer();
              }
            }}
        >
          <span className="bank-account-selected-main">{displayNumber || (disabled ? "Bank only" : "Select bank account")}</span>
          {bankLabel ? <span className="bank-account-selected-bank">{bankLabel}</span> : null}
          {displayName ? <span className="bank-account-selected-name">{displayName}</span> : null}
          {!showSearchButton ? <DownOutlined className="smart-lookup-caret bank-account-caret" /> : null}
          </div>
        </Tooltip>
        {showSearchButton ? <Button disabled={disabled} icon={<SearchOutlined />} onClick={openDrawer} tabIndex={-1} aria-label="Select bank account" /> : null}
      </Space.Compact>
      <Drawer
        title={<Space><BankOutlined />Select Bank Account</Space>}
        open={open}
        onClose={closeDrawer}
        width={520}
        zIndex={1900}
        autoFocus={false}
        className="bank-account-grouped-panel"
      >
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          <Input.Search ref={searchRef} value={q} onChange={(event) => setQ(event.target.value)} onKeyDown={handleBankSearchKeyDown} placeholder="Search bank..." />
          <Typography.Text type="secondary">Choose an account from a bank group. Selection fills both Bank and Bank A/C.</Typography.Text>
          {banksQuery.isLoading ? <Spin /> : !banks.length ? <Empty description="No banks found" /> : (
            <Collapse
              accordion
              activeKey={activeBankKey}
              onChange={(key) => {
                const nextKey = Array.isArray(key) ? key[0] : key;
                setActiveBankKey(nextKey ? String(nextKey) : undefined);
                setAccountFocusBankKey(undefined);
              }}
              items={banks.map((bank) => ({
                key: String(bankId(bank)),
                label: <strong className={String(bankId(bank)) === activeBankKey ? "bank-account-bank-active" : undefined}>{bankName(bank)}</strong>,
                children: (
                  <BankAccountsForBank
                    bank={bank}
                    autoFocus={accountFocusBankKey === String(bankId(bank))}
                    onSelect={(account) => {
                      const selectedBankLabel = accountBankName(account, bank);
                      const selectedAccountNumber = readText(account, ["accountNumber", "AccountNumber"]);
                      const selectedAccountName = readText(account, ["accountName", "AccountName"]);
                      onSelect({
                        bankId: accountBankId(account, bank),
                        bankLabel: selectedBankLabel,
                        bankAccountId: accountId(account),
                        bankAccountLabel: accountDisplay(account, selectedBankLabel),
                        bankAccountNumber: selectedAccountNumber,
                        bankAccountName: selectedAccountName,
                        accountLedgerId: readId(account, ["accountLedgerId", "AccountLedgerId"])
                      });
                      closeDrawer();
                    }}
                  />
                )
              }))}
            />
          )}
        </Space>
      </Drawer>
    </>
  );
}

function BankAccountsForBank({
  bank,
  autoFocus,
  onSelect
}: {
  bank: BankGroupRow;
  autoFocus?: boolean;
  onSelect: (account: BankAccountRow) => void;
}) {
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    if (!autoFocus || !bank.accounts.length) return;
    requestAnimationFrame(() => itemRefs.current[0]?.focus());
  }, [autoFocus, bank.accounts.length]);

  const focusAccount = (index: number) => {
    itemRefs.current[index]?.focus();
  };

  if (!bank.accounts.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No accounts" />;

  return (
    <List
      size="small"
      dataSource={bank.accounts}
      renderItem={(account, index) => (
        <List.Item
          className="bank-account-row"
          ref={(element) => {
            itemRefs.current[index] = element;
          }}
          tabIndex={0}
          onClick={() => onSelect(account)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              focusAccount(Math.min(index + 1, bank.accounts.length - 1));
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              focusAccount(Math.max(index - 1, 0));
            }
            if (event.key === "Enter") {
              event.preventDefault();
              onSelect(account);
            }
          }}
        >
          <List.Item.Meta
            title={(
              <Space size={8} wrap>
                <span>{accountDisplay(account, bankName(bank))}</span>
                <Typography.Text type="secondary" className="bank-account-bank-label">
                  {bankName(bank)}
                </Typography.Text>
              </Space>
            )}
            description={accountDescription(account)}
          />
        </List.Item>
      )}
    />
  );
}
